import { assets } from '$app/paths';
import stringTemplateHelper from '$lib/helpers/string-template-helper';

/** This helper contains numerous functions to aid in the rendering of the country detection modal */
const countryDetectionHelper = {
  /**
   * Extracts the geolocated country from a geocode in the form XX-XX, where the neither part has a length limit.
   * Returns null if the input is malformed
   *
   * @param geoCode
   * @returns String
   */
  extractCountryFromGeoCode(geoCode: string | null): string | null {
    if (!geoCode || typeof geoCode !== 'string') {
      return null;
    }

    const parts = geoCode.split('-');
    if (parts.length !== 2 || !parts[0]) {
      return null;
    }

    return parts[0].toLowerCase();
  },
  /**
   * Generates a set of supported countries in shorthand form Currently set only includes au, ca, es, fr, gb, ie, jp, us
   *
   * @param countriesAndLangs
   * @returns Set<string>
   */
  generateSupportedCountryCodes(countriesAndLangs: CountryAndLanguageNamesMap): Set<string> {
    const supportedCountryCodes = new Set<string>();

    for (const country in countriesAndLangs) {
      for (const countryData of countriesAndLangs[country]) {
        supportedCountryCodes.add(countryData.countryTwoLetterCode.toLowerCase());
      }
    }

    return supportedCountryCodes;
  },
  /**
   * Extracts country from locale code
   *
   * @param requestedLocale
   * @returns String
   *
   *   Example usage: fr-ca => ca us => en ja => jp
   */
  extractCountryCodeFromLocale(requestedLocale: string) {
    switch (requestedLocale) {
      case 'en': {
        return 'us';
      }
      case 'ja': {
        return 'jp';
      }
      default: {
        return requestedLocale.split('-')[1].toLowerCase();
      }
    }
  },
  /**
   * Extracts language from locale code
   *
   * @param requestedLocale
   * @returns String
   *
   *   Example usage: en => en ja => ja fr-CA => fr
   */
  extractLanguageCodeFromLocale(requestedLocale: string) {
    if (requestedLocale.length === 2) {
      return requestedLocale;
    } else {
      return requestedLocale.split('-')[0].toLowerCase();
    }
  },
  /**
   * Sanitizes browser language settings and Contentful locales into Square locales
   *
   * @param locale
   * @returns String
   *
   *   Example usage: 'ca' => 'ca-ES' (must be converted to Square Catalan locale code) 'en-US' => 'en' (must be converted
   *   to Square US locale code) 'fr-CA' => 'fr-CA'
   */
  reformatLocale(locale: string): string {
    switch (locale) {
      case 'ca': {
        return 'ca-ES';
      }
      case 'en-US': {
        return 'en';
      }
      default: {
        return locale;
      }
    }
  },
  /**
   * Gets the country detection modal title from the provided translations
   *
   * @param translations
   * @returns String | undefined
   */
  getTitle(translations: Record<string, any>): string | undefined {
    try {
      return translations['country_detection']['heading'];
    } catch (e) {
      throw new Error(
        `Could not find key [country_detection][heading] in translations provided: ${e instanceof Error ? e.message : ''}`
      );
    }
  },
  /**
   * Gets the country detection available locales heading from the provided translations
   *
   * @param translations
   * @returns
   */
  getAvailableLocalesHeading(translations: Record<string, any>): string | undefined {
    try {
      return translations['country_detection']['available_locales_heading'];
    } catch (e) {
      throw new Error(
        `Could not find key [country_detection][available_locales_heading] in translations provided: ${e instanceof Error ? e.message : ''}`
      );
    }
  },
  /**
   * Gets the translated proceed text with the following logic:
   *
   * - If the user's preferred locale is supported by Square, the proceed text will be translated with the preferred
   *   locale
   * - If the user's preferred locale is not supported, the proceed text will be translated according to the requested
   *   locale
   *
   * @param browserPreferredLocale
   * @param requestedLocale
   * @param supportedLocales
   * @param translations
   * @returns
   */
  getProceedText(
    browserPreferredLocale: string,
    requestedLocale: string,
    supportedLocales: Array<string>,
    translations: Record<string, any>
  ): string | undefined {
    const requestedCountryCode = this.extractCountryCodeFromLocale(requestedLocale);
    const requestedLanguageCode = this.extractLanguageCodeFromLocale(requestedLocale);
    let proceedText: string = '';

    // User specifies a preferred browser language that is supported
    // Text translated according to user's browser language preference
    if (browserPreferredLocale && supportedLocales.includes(browserPreferredLocale)) {
      if (translations['country_detection']['proceed_link_text']) {
        proceedText = stringTemplateHelper.injectValueToTranslationStringTemplate(
          translations['country_detection']['proceed_link_text'],
          {
            country_and_language: this.getTranslatedCountryAndLanguage(
              browserPreferredLocale,
              requestedCountryCode,
              requestedLanguageCode,
              translations
            ),
          }
        );
      }
    }
    // Preferred browser language isn't supported or user doesn't specify a preferred browser language.
    // Text is translated according to the language specified in the request parameter.
    else {
      if (translations['country_detection']['proceed_link_text']) {
        proceedText = stringTemplateHelper.injectValueToTranslationStringTemplate(
          translations['country_detection']['proceed_link_text'],
          {
            country_and_language: this.getTranslatedCountryAndLanguage(
              requestedLocale,
              requestedCountryCode,
              requestedLanguageCode,
              translations
            ),
          }
        );
      }
    }

    return proceedText;
  },
  /**
   * Generates the URL for the switch country button URL according to the user geolocated country and default language
   * code for said country
   *
   * @param currentPath
   * @param userGeolocatedCountryCode
   * @param defaultLanguageCode
   * @returns
   */
  getSwitchCountryButtonUrl(
    currentPath: string,
    userGeolocatedCountryCode: string,
    defaultLanguageCode: string
  ): string {
    // https://regexr.com/7ab78
    // https://squareup.com/us/en/sales => https://squareup.com/ca/en/sales?country_redirection=true
    return `${currentPath.replace(/\/\w+\/\w+/, `/${userGeolocatedCountryCode}/${defaultLanguageCode}`)}?country_redirection=true`;
  },

  /**
   * Gets the translations for the country detection If the browser preferred locale exists and is a Square-supported
   * locale, return translations in that locale Else, return translations in the requested locale
   *
   * @param requestedLocale
   * @param browserLanguagePreferredLocale
   * @param supportedLocales
   * @returns
   */
  async getTranslations(requestedLocale: string, browserLanguagePreferredLocale: string, supportedLocales: string[]) {
    const path =
      browserLanguagePreferredLocale && supportedLocales.includes(browserLanguagePreferredLocale)
        ? `/translations/manual/${browserLanguagePreferredLocale}.json`
        : `/translations/manual/${requestedLocale}.json`;

    const res = await fetch(`${assets}${path}`);

    return res.json();
  },
  /**
   * Detects whether the requested locale and user geolocation are mismatched Summary of country detection logic: User
   * visits a page from a country that isn’t supported by Square
   *
   * - Country detection modal isn't displayed User visits a page from a Square supported country (country specified in
   *   the request parameter and the user’s detected country matches)
   * - Country detection modal isn't displayed User visits a page from a Square supported country (country specified in
   *   the request parameter and the user’s country doesn’t match)
   * - Country detection modal is displayed The geolocation cookie is not included in the response headers
   * - Country detection modal is not displayed
   *
   * @param requestedLocale
   * @param userGeolocatedCountryCode
   * @param countriesAndLangs
   * @returns Boolean
   *
   *   Example usage: locale = '/nz/en', user geolocation = 'US-NY' => false locale = 'us/en/', user geolocation = 'US-NY'
   *   => false locale = 'au/en', user geolocation = 'US-NY' => true
   */
  detectCountryMismatch(
    requestedLocale: string,
    userGeolocatedCountryCode: string,
    countriesAndLangs: CountryAndLanguageNamesMap
  ): boolean {
    const requestedCountryCode: string = this.extractCountryCodeFromLocale(requestedLocale);
    const geolocatedCountryCode: string = userGeolocatedCountryCode.toLowerCase();
    const supportedCountryCodes = this.generateSupportedCountryCodes(countriesAndLangs);

    // User visits a page from supported country, geolocation & locale don't match
    if (supportedCountryCodes.has(geolocatedCountryCode) && requestedCountryCode !== geolocatedCountryCode) {
      return true;
    }

    return false;
  },
  /**
   * Returns an object containing the title, available locales headings, proceed text, switch country button text and
   * URL for the country detection modal Calls:
   *
   * - GetTitle
   * - GetAvailableLocalesHeading
   * - GetTranslatedCountryAndLanguage
   * - GetSwitchCountryButtonUrl
   * - GetProceedText
   *
   * @param requestedLocale
   * @param userGeolocatedCountryCode
   * @param browserPreferredLocale
   * @param supportedLocales
   * @param defaultLocales
   * @param currentPath
   * @returns Promise<CountryDetectionModalStrings>
   */
  async getLocalizedCountryDetectionModalStringsAndUrls(
    requestedLocale: string,
    userGeolocatedCountryCode: string,
    browserPreferredLocale: string,
    supportedLocales: Array<string>,
    defaultLocales: Record<string, string>,
    currentPath: string
  ): Promise<CountryDetectionModalStrings> {
    const formattedRequestedLocale: string = this.reformatLocale(requestedLocale);
    const formattedBrowserPreferredLocale: string = this.reformatLocale(browserPreferredLocale);

    const translations: Record<string, any> = await this.getTranslations(
      formattedRequestedLocale,
      formattedBrowserPreferredLocale,
      supportedLocales
    );

    // Modal switch country button text and URL are localized according to the geolocated country's default locale
    const defaultLocale: string = defaultLocales[userGeolocatedCountryCode];
    const defaultLanguageCode: string = this.extractLanguageCodeFromLocale(defaultLocale);

    const switchCountryButtonTranslations: Record<string, any> = await this.getTranslations(
      defaultLocale,
      defaultLocale,
      supportedLocales
    );

    return {
      title: this.getTitle(translations),
      availableLocalesHeading: this.getAvailableLocalesHeading(translations),
      switchCountryButtonText: this.getTranslatedCountryAndLanguage(
        defaultLocale,
        userGeolocatedCountryCode,
        defaultLanguageCode,
        switchCountryButtonTranslations
      ),
      switchCountryButtonUrl: this.getSwitchCountryButtonUrl(
        currentPath,
        userGeolocatedCountryCode,
        defaultLanguageCode
      ),
      proceedText: this.getProceedText(
        formattedBrowserPreferredLocale,
        formattedRequestedLocale,
        supportedLocales,
        translations
      ),
    };
  },
  /**
   * Translates the country name and language to the provided locale
   *
   * @param locale ({langCode}-{countryCode})
   * @param countryCode
   * @param languageCode
   * @returns String
   *
   *   Example usage: locale: fr-CA countryCode: jp languageCode: ja => Japon (Japonais)
   */
  getTranslatedCountryAndLanguage(
    locale: string,
    countryCode: string = 'us',
    languageCode: string,
    translations: Record<string, any>
  ): string | undefined {
    let country: string | undefined, language: string | undefined;

    try {
      country = translations['localized_supported_country_names'][countryCode];
    } catch (e) {
      throw new Error(
        `Not able to retrieve translated country with given country code: ${e instanceof Error ? e.message : ''}.`
      );
    }
    try {
      language = translations['localized_supported_language_names'][languageCode];
    } catch (e) {
      throw new Error(
        `Not able to retrieve translated language with given language code: ${e instanceof Error ? e.message : ''}.`
      );
    }

    return `${country} (${language})`;
  },
};

export default countryDetectionHelper;
